import {
  DEFAULT_SQL_QUERY,
  MAX_POINTS,
  SQL_ENGINE_VERSION,
} from './model.js';

export const MAX_SQL_LENGTH = 8000;
export const MAX_SQL_RESULT_ROWS = 50000;

export const SQL_PRESETS = Object.freeze({
  raw: `SELECT
  timestamp,
  series_id,
  value
FROM readings
WHERE timestamp BETWEEN $start_ms AND $end_ms
ORDER BY timestamp, series_id
LIMIT 50000`,
  hourly: DEFAULT_SQL_QUERY,
  filtered: `SELECT
  CAST((timestamp - $start_ms) / 3600000 AS INTEGER) * 3600000 + $start_ms AS timestamp,
  series_id,
  ROUND(AVG(value), 3) AS value
FROM readings
WHERE timestamp BETWEEN $start_ms AND $end_ms
  AND value >= 45
GROUP BY 1, series_id
HAVING COUNT(value) > 0
ORDER BY 1, series_id
LIMIT 50000`,
});

export function normalizeSqlText(sql) {
  if (typeof sql !== 'string') throw new TypeError('SQL query must be a string.');
  const trimmed = sql.trim();
  if (!trimmed) throw new TypeError('SQL query cannot be empty.');
  if (trimmed.length > MAX_SQL_LENGTH) {
    throw new TypeError(`SQL query cannot exceed ${MAX_SQL_LENGTH} characters.`);
  }
  const withoutTrailingSemicolon = trimmed.endsWith(';')
    ? trimmed.slice(0, -1).trimEnd()
    : trimmed;
  if (withoutTrailingSemicolon.includes(';')) {
    throw new TypeError('Run one SQL statement at a time.');
  }
  if (!/^(SELECT|WITH)\b/i.test(withoutTrailingSemicolon)) {
    throw new TypeError('Only read-only SELECT or WITH queries are allowed.');
  }
  return withoutTrailingSemicolon;
}

function createReadings(config, sourceSnapshot) {
  const seriesById = new Map(config.series.map((series) => [series.id, series]));
  const readings = [];
  sourceSnapshot.source.forEach((row) => {
    const timestamp = row[0];
    sourceSnapshot.dimensions.slice(1).forEach((seriesId, index) => {
      const series = seriesById.get(seriesId);
      readings.push({
        timestamp,
        timestampIso: new Date(timestamp).toISOString(),
        seriesId,
        seriesName: series?.name ?? seriesId,
        value: row[index + 1],
      });
    });
  });
  return readings;
}

function findColumn(row, name) {
  const key = Object.keys(row).find((candidate) => candidate.toLowerCase() === name);
  return key === undefined ? undefined : row[key];
}

function pivotResultRows(rows, config) {
  const seriesIds = config.series.map((series) => series.id);
  const allowedSeries = new Set(seriesIds);
  const byTimestamp = new Map();
  const seenCells = new Set();

  rows.forEach((row, index) => {
    const timestamp = Number(findColumn(row, 'timestamp'));
    const seriesId = String(findColumn(row, 'series_id') ?? '');
    const rawValue = findColumn(row, 'value');
    const value = rawValue === null ? null : Number(rawValue);

    if (!Number.isSafeInteger(timestamp)) {
      throw new TypeError(`SQL result row ${index + 1} has an invalid timestamp.`);
    }
    if (!allowedSeries.has(seriesId)) {
      throw new TypeError(`SQL result row ${index + 1} has unknown series_id “${seriesId}”.`);
    }
    if (value !== null && !Number.isFinite(value)) {
      throw new TypeError(`SQL result row ${index + 1} has a non-numeric value.`);
    }

    const cellKey = `${timestamp}\u0000${seriesId}`;
    if (seenCells.has(cellKey)) {
      throw new TypeError('SQL results must contain at most one row per timestamp and series_id. Add GROUP BY when joining or aggregating.');
    }
    seenCells.add(cellKey);

    if (!byTimestamp.has(timestamp)) {
      byTimestamp.set(timestamp, [timestamp, ...seriesIds.map(() => null)]);
    }
    byTimestamp.get(timestamp)[seriesIds.indexOf(seriesId) + 1] = value;
  });

  const source = [...byTimestamp.values()].sort((left, right) => left[0] - right[0]);
  if (source.length > MAX_POINTS) {
    throw new TypeError(`SQL output cannot exceed ${MAX_POINTS} chart timestamps.`);
  }

  return {
    kind: 'sql-result-v1',
    dimensions: ['timestamp', ...seriesIds],
    source,
  };
}

export function executeSqlWithEngine(SQL, config, sourceSnapshot, sql) {
  const normalizedSql = normalizeSqlText(sql);
  const readings = createReadings(config, sourceSnapshot);
  const database = new SQL.Database();

  try {
    database.run(`CREATE TABLE readings (
      timestamp INTEGER NOT NULL,
      timestamp_iso TEXT NOT NULL,
      series_id TEXT NOT NULL,
      series_name TEXT NOT NULL,
      value REAL
    )`);
    database.run('CREATE INDEX readings_time_series ON readings(timestamp, series_id)');
    const insert = database.prepare('INSERT INTO readings VALUES (?, ?, ?, ?, ?)');
    try {
      database.run('BEGIN');
      readings.forEach((reading) => {
        insert.run([
          reading.timestamp,
          reading.timestampIso,
          reading.seriesId,
          reading.seriesName,
          reading.value,
        ]);
      });
      database.run('COMMIT');
    } catch (error) {
      database.run('ROLLBACK');
      throw error;
    } finally {
      insert.free();
    }

    const versionResult = database.exec('SELECT sqlite_version() AS version');
    const sqliteVersion = versionResult[0]?.values?.[0]?.[0] ?? 'unknown';
    database.run('PRAGMA query_only = ON');

    const statement = database.prepare(normalizedSql);
    const rows = [];
    try {
      statement.bind({
        $start_ms: config.time.startMs,
        $end_ms: config.time.endMs,
        $interval_ms: config.time.intervalMs,
      });
      while (statement.step()) {
        rows.push(statement.getAsObject());
        if (rows.length > MAX_SQL_RESULT_ROWS) {
          throw new TypeError(`SQL result cannot exceed ${MAX_SQL_RESULT_ROWS} rows. Add or lower LIMIT.`);
        }
      }
    } finally {
      statement.free();
    }

    if (rows.length > 0) {
      const columns = Object.keys(rows[0]).map((column) => column.toLowerCase());
      for (const required of ['timestamp', 'series_id', 'value']) {
        if (!columns.includes(required)) {
          throw new TypeError(`SQL result must include a ${required} column.`);
        }
      }
    }

    const snapshot = pivotResultRows(rows, config);
    return {
      snapshot,
      queryResult: {
        enabled: true,
        engine: 'sql.js',
        packageVersion: SQL_ENGINE_VERSION,
        sqliteVersion: String(sqliteVersion),
        inputRows: readings.length,
        selectedRows: rows.length,
        outputPoints: snapshot.source.length,
        chartOrder: 'timestamp-ascending',
        parameters: {
          startMs: config.time.startMs,
          endMs: config.time.endMs,
          intervalMs: config.time.intervalMs,
        },
      },
    };
  } finally {
    database.close();
  }
}

export function createPassthroughQueryResult(config, sourceSnapshot) {
  return {
    snapshot: {
      kind: 'sql-result-v1',
      dimensions: [...sourceSnapshot.dimensions],
      source: sourceSnapshot.source.map((row) => [...row]),
    },
    queryResult: {
      enabled: false,
      engine: null,
      packageVersion: SQL_ENGINE_VERSION,
      sqliteVersion: null,
      inputRows: sourceSnapshot.source.length * config.series.length,
      selectedRows: sourceSnapshot.source.length * config.series.length,
      outputPoints: sourceSnapshot.source.length,
      chartOrder: 'timestamp-ascending',
      parameters: {
        startMs: config.time.startMs,
        endMs: config.time.endMs,
        intervalMs: config.time.intervalMs,
      },
    },
  };
}
